import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import BoatCard from "@/components/BoatCard";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Horizontal, user-driven boat carousel (drag / scroll / arrows). No auto-scroll.
 * Consumes the SAME endpoint as Explore (/api/explore/boats).
 */
export default function BoatMarquee() {
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/explore/boats")
      .then((r) => setBoats(r.data || []))
      .catch(() => setBoats([]))
      .finally(() => setLoading(false));
  }, []);

  const scrollBy = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.8, 760), behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="mt-14 flex gap-8 overflow-hidden px-6 lg:px-10 max-w-7xl mx-auto" data-testid="fleet-marquee-loading">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="w-[280px] sm:w-[320px] md:w-[360px] shrink-0">
            <div className="aspect-[4/5] rounded-2xl bg-slate-100 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (boats.length === 0) return null;

  return (
    <div className="mt-10 relative" data-testid="fleet-marquee">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex justify-end gap-2 mb-4">
        <button onClick={() => scrollBy(-1)} className="carousel-arrow" aria-label="Anterior" data-testid="fleet-carousel-prev">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button onClick={() => scrollBy(1)} className="carousel-arrow" aria-label="Siguiente" data-testid="fleet-carousel-next">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div ref={ref} className="carousel-viewport" data-testid="fleet-carousel-track">
        <div className="flex gap-8 w-max px-6 lg:px-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))]">
          {boats.map((b) => (
            <div key={b.id} className="snap-start">
              <BoatCard boat={b} variant="marquee" testIdPrefix="fleet-marquee-card" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
